import type { ReactNode } from 'react';

/**
 * A page section that can be folded away.
 *
 * The heading is the control — the whole row is clickable rather than a small
 * chevron beside it — and the count stays visible while collapsed, so a folded
 * section still says how much is inside.
 */
export function CollapsibleSection({
  title,
  count,
  open,
  onToggle,
  children,
}: {
  title: string;
  /** Shown beside the title; omit for a section with nothing to count. */
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className="mb-8">
      <h2 className="mb-4">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="group flex w-full items-center gap-2 border-b border-field-800 py-1 text-left"
        >
          <span className="section-title group-hover:bg-brass-300">{title}</span>
          {count === undefined ? null : (
            <span className="badge border border-field-700 text-field-300">{count}</span>
          )}
          <span
            aria-hidden
            className={`ml-auto pr-1 font-display text-xl leading-none text-brass-400
              transition-transform ${open ? 'rotate-90' : ''}`}
          >
            ›
          </span>
        </button>
      </h2>

      {open ? children : null}
    </section>
  );
}
